/*eslint-disable*/
import React from 'react';
// nodejs library to set properties for components
// nodejs library that concatenates classes
// material-ui core components
import { makeStyles } from '@mui/styles';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import discordImg from 'assets/img/discord.png';
import { useTheme } from '../../context/ThemeContext';
import { LanguageSelector } from '../LanguageSelector/LanguageSelector';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import { primaryColor, secondaryColor } from '../../assets/jss/material-kit-react';
import { Typography } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Paper from '@mui/material/Paper';
import Grow from '@mui/material/Grow';
import Popper from '@mui/material/Popper';
import Button from 'components/CustomButtons/Button.js';
import classNames from 'classnames';

const useStyles = makeStyles((theme) => ({
  footer: {
    display: 'flex',
    width: '100%',
    padding: '10px 0',
    backgroundColor: '#212121',
    color: '#ffffff',
    boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
    marginTop: '24px',
  },
  container: {
    paddingRight: '15px',
    paddingLeft: '15px',
    marginRight: 'auto',
    marginLeft: 'auto',
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    paddingBottom: '16px',
    '@media (min-width: 498px)': {
      paddingBottom: '0px',
    },
    '@media (min-width: 576px)': {
      maxWidth: '540px',
    },
    '@media (min-width: 768px)': {
      maxWidth: '100%',
      paddingLeft: '48px',
      paddingRight: '76px',
      justifyContent: 'space-between',
    },
  },
  left: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    zIndex: 4,
  },
  right: {
    padding: '8px 0',
    margin: '0',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    position: 'relative',
    zIndex: 1,
  },
  list: {
    marginBottom: '0',
    padding: '0',
    marginTop: '0',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    zIndex: '10000',
  },
  inlineBlock: {
    display: 'inline-block',
    padding: '0px',
    width: 'auto',
  },
  link: {
    color: theme?.palette?.mode === 'dark' ? '#ffffff' : primaryColor,
    textDecoration: 'none',
    backgroundColor: 'transparent',
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    transition: 'opacity 0.2s ease-in-out',
    position: 'relative',
    zIndex: 1,
    '&:hover': {
      opacity: 0.8,
    },
  },
  dropdownWrapper: {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
  },
  caret: {
    transition: 'all 150ms ease-in',
    display: 'inline-block',
    width: 0,
    height: 0,
    marginLeft: '4px',
    verticalAlign: 'middle',
    borderTop: '4px solid',
    borderRight: '4px solid transparent',
  },
  dropdownContent: {
    position: 'absolute',
    bottom: '100%',
    transform: 'translateX(-50%)',
    left: '50%',
    marginBottom: '48px',
    minWidth: '160px',
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[2],
    borderRadius: theme.shape.borderRadius,
    zIndex: 1301,
  },
  themeOption: {
    color: 'inherit',
    padding: '0.75rem 1.25rem 0.75rem 0.75rem',
    fontWeight: 400,
    fontSize: '12px',
    textTransform: 'uppercase',
    borderRadius: '4px',
    lineHeight: '20px',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    textDecoration: 'none',
    backgroundColor: 'transparent',
    backdropFilter: 'none',
    boxShadow: 'none',
    transition: 'all .3s',
    width: '100%',
    textAlign: 'left',
    cursor: 'pointer',
    border: '1px solid transparent !important',
    '&:hover,&:focus': {
      backgroundColor: `${theme.palette.outlined.secondary} !important`,
      borderColor: secondaryColor,
      boxShadow: 'none !important',
      border: `1px solid ${secondaryColor} !important`,
      borderRadius: '3px',
    },
  },
  popper: {
    position: 'absolute',
    zIndex: 1301,
    '&[data-popper-placement*="bottom"]': {
      transform: 'none !important',
    },
    '&[data-popper-placement*="top"]': {
      transform: 'none !important',
    },
  },
  show: {
    pointerEvents: 'auto',
  },
  menuList: {
    padding: '8px 4px !important',
  },
  buttonWrapper: {
    display: 'inline-flex',
    alignItems: 'center',
  },
  '@global': {
    'html, body': {
      height: '100%',
      margin: 0,
      padding: 0,
    },
    body: {
      minHeight: '100vh',
    },
    '#root': {
      minHeight: '100vh',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    },
    main: {
      flex: '1',
    },
  },
}));

export default function Footer() {
  const classes = useStyles();
  const { isDarkMode, toggleDarkMode } = useTheme();
  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleMouseEnter = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMouseLeave = () => {
    setAnchorEl(null);
  };

  const themeOptions = [
    {
      icon: <LightModeIcon sx={{ fontSize: 16, verticalAlign: 'middle' }} />,
      text: 'Light Mode',
      action: () => isDarkMode && toggleDarkMode(),
    },
    {
      icon: <DarkModeIcon sx={{ fontSize: 16, verticalAlign: 'middle' }} />,
      text: 'Dark Mode',
      action: () => !isDarkMode && toggleDarkMode(),
    },
  ];

  return (
    <footer className={classes.footer}>
      <div className={classes.container}>
        <div className={classes.left}>
          <List className={classes.list}>
            <ListItem className={classes.inlineBlock}>
              <div
                className={classes.dropdownWrapper}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                <Button
                  aria-label="Theme Selector"
                  aria-owns={anchorEl ? 'menu-list' : undefined}
                  aria-haspopup="true"
                  color="transparent"
                >
                  <span className={classes.buttonWrapper}>
                    {isDarkMode ? (
                      <DarkModeIcon sx={{ fontSize: 16, verticalAlign: 'middle' }} />
                    ) : (
                      <LightModeIcon sx={{ fontSize: 16, verticalAlign: 'middle' }} />
                    )}
                    <b
                      className={classes.caret}
                      style={{ transform: anchorEl ? 'rotate(45deg)' : 'rotate(225deg)' }}
                    />
                  </span>
                </Button>
                <Popper
                  open={Boolean(anchorEl)}
                  anchorEl={anchorEl}
                  transition
                  disablePortal
                  placement="top"
                  modifiers={[
                    {
                      name: 'offset',
                      options: {
                        offset: [0, 48],
                      },
                    },
                    {
                      name: 'flip',
                      enabled: false,
                    },
                  ]}
                  className={classNames(classes.popper, {
                    [classes.show]: Boolean(anchorEl),
                  })}
                >
                  {({ TransitionProps }) => (
                    <Grow
                      {...TransitionProps}
                      style={{ transformOrigin: 'center bottom' }}
                      timeout={200}
                    >
                      <Paper
                        className={classes.dropdownContent}
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}
                      >
                        <ClickAwayListener onClickAway={handleMouseLeave}>
                          <MenuList role="menu" className={classes.menuList}>
                            {themeOptions.map((option, index) => (
                              <MenuItem key={index} onClick={option.action} style={{ padding: 0 }}>
                                <button
                                  className={classes.themeOption}
                                  type="button"
                                  role="menuitem"
                                >
                                  {option.icon}
                                  <Typography variant="inherit" style={{ paddingLeft: '4px' }}>
                                    {option.text}
                                  </Typography>
                                </button>
                              </MenuItem>
                            ))}
                          </MenuList>
                        </ClickAwayListener>
                      </Paper>
                    </Grow>
                  )}
                </Popper>
              </div>
            </ListItem>
            <ListItem className={classes.inlineBlock}>
              <LanguageSelector />
            </ListItem>
          </List>
        </div>
        <div className={classes.right}>
          <a
            href="https://discord.gg/UQvyYd93Hz"
            className={classes.link}
            rel="noreferrer"
            target="_blank"
          >
            <img style={{ width: '86px' }} src={discordImg} alt="discord" />
          </a>
          {/* <div style={{ fontSize: '12px' }}>
            &copy; {1900 + new Date().getYear()}{' '}
            <a
              href="https://www.d2rs.io"
              className={classes.link}
              target="_blank"
              rel="noreferrer"
              style={{ color: 'white' }}
            >
              d2rs.io
            </a>
          </div> */}
        </div>
      </div>
    </footer>
  );
}
